import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  disableFragmentWarnings,
  from,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { GenericError } from '@auth0/auth0-react';
import { Logtail } from '@logtail/browser';
import { fromUnixTime, isAfter, sub } from 'date-fns';
import { CloseCode, createClient } from 'graphql-ws';
import { JwtPayload, jwtDecode } from 'jwt-decode';
import { useMemo, useState } from 'react';

import useAccessToken from 'hooks/useAccessToken';
import useLogtail from 'hooks/useLogtail';

// Disable warnings about duplicated fragments since we trigger it with our
// generated types. A few warnings may still appear on page load from documents
// parsed before this module loads. Those warnings are not indicative of an issue.
disableFragmentWarnings();

function getHostname() {
  const hostname = import.meta.env.REACT_APP_GRAPHQL_HOST;

  if (import.meta.env.REACT_APP_DEPLOY_ENV === 'prod') {
    return 'https://gql.syllabird.com/v1/graphql';
  } else if (import.meta.env.REACT_APP_DEPLOY_ENV === 'test') {
    return `https://${hostname}.onrender.com/v1/graphql`;
  } else {
    return `http://${hostname}/v1/graphql`;
  }
}

function getWsHostname() {
  const hostname = import.meta.env.REACT_APP_GRAPHQL_HOST;

  if (import.meta.env.REACT_APP_DEPLOY_ENV === 'prod') {
    return 'wss://gql.syllabird.com/v1/graphql';
  } else if (import.meta.env.REACT_APP_DEPLOY_ENV === 'test') {
    return `wss://${hostname}.onrender.com/v1/graphql`;
  } else {
    return `ws://${hostname}/v1/graphql`;
  }
}

class AuthTokenError extends Error {
  readonly originalError: unknown;

  constructor(originalError: unknown) {
    super(
      originalError instanceof Error
        ? originalError.message
        : String(originalError)
    );
    this.name = 'AuthTokenError';
    this.originalError = originalError;
  }
}

function getErrorDetails(error: unknown) {
  if (error instanceof GenericError) {
    return {
      name: error.name,
      message: error.message,
      code: error.error,
      description: error.error_description,
    };
  }

  if (error instanceof Error) {
    return { name: error.name, message: error.message };
  }

  return { message: String(error) };
}

function getClientContext() {
  return {
    online: navigator.onLine,
    visibility: document.visibilityState,
    msSincePageLoad: Math.round(performance.now()),
  };
}

interface AuthTokenState {
  httpToken: string | null;
  httpTokenExpiration: Date;
  wsToken: string | null;
  wsTokenExpiration: Date;
}

interface AuthApolloClient {
  client: ApolloClient<NormalizedCacheObject>;
  resetClient: () => Promise<void>;
}

function createAuthApolloClient(
  cache: InMemoryCache,
  getAccessToken: () => Promise<string>,
  logtail: Logtail
): AuthApolloClient {
  const tokens: AuthTokenState = {
    httpToken: null,
    httpTokenExpiration: new Date(),
    wsToken: null,
    wsTokenExpiration: new Date(),
  };

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    const definition = getMainDefinition(operation.query);
    const operationInfo = {
      name: operation.operationName,
      type:
        definition.kind === 'OperationDefinition'
          ? definition.operation
          : undefined,
    };

    graphQLErrors?.forEach((graphQLError) => {
      if (!graphQLError.message.includes('JWTExpired')) {
        logtail.error('graphql_error', {
          operation: operationInfo,
          error: {
            message: graphQLError.message,
            code: graphQLError.extensions?.code,
          },
        });

        if (import.meta.env.REACT_APP_DEPLOY_ENV !== 'prod') {
          console.error('GraphQL Error:', graphQLError);
        }
      }
    });

    if (networkError) {
      if (networkError instanceof AuthTokenError) {
        logtail.error('auth_token_error', {
          transport: 'http',
          operation: operationInfo,
          error: getErrorDetails(networkError.originalError),
          client: getClientContext(),
        });
      } else {
        logtail.error('graphql_network_error', {
          operation: operationInfo,
          error: {
            ...getErrorDetails(networkError),
            statusCode:
              'statusCode' in networkError
                ? networkError.statusCode
                : undefined,
          },
          client: getClientContext(),
        });
      }

      if (import.meta.env.REACT_APP_DEPLOY_ENV !== 'prod') {
        console.error('Network Error:', networkError);
      }
    }
  });

  const retryLink = new RetryLink({
    delay: {
      initial: 300,
    },
    attempts: {
      max: 3,
    },
  });

  const getHttpUserToken = setContext(async () => {
    // We check if the http token expires in one minute to leave extra room for
    // the time it takes to make a network request
    if (
      !tokens.httpToken ||
      isAfter(new Date(), sub(tokens.httpTokenExpiration, { minutes: 1 }))
    ) {
      try {
        tokens.httpToken = await getAccessToken();
      } catch (error) {
        throw new AuthTokenError(error);
      }

      const decodedUserToken = jwtDecode<JwtPayload>(tokens.httpToken);
      tokens.httpTokenExpiration = decodedUserToken.exp
        ? fromUnixTime(decodedUserToken.exp)
        : new Date();
    }
  });

  const resetExpiredHttpUserToken = onError(({ graphQLErrors }) => {
    graphQLErrors?.forEach(({ message }) => {
      if (message.includes('JWTExpired')) {
        tokens.httpToken = null;
      }
    });
  });

  const getHttpAuthHeadersLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        authorization: tokens.httpToken ? `Bearer ${tokens.httpToken}` : null,
      },
    };
  });

  const httpLink = new HttpLink({
    uri: getHostname(),
  });

  const wsLink = new GraphQLWsLink(
    createClient({
      url: getWsHostname(),
      connectionParams: async () => {
        if (!tokens.wsToken) {
          try {
            tokens.wsToken = await getAccessToken();
          } catch (error) {
            logtail.error('auth_token_error', {
              transport: 'websocket',
              error: getErrorDetails(error),
              client: getClientContext(),
            });
            throw error;
          }

          const decodedUserToken = jwtDecode<JwtPayload>(tokens.wsToken);
          tokens.wsTokenExpiration = decodedUserToken.exp
            ? fromUnixTime(decodedUserToken.exp)
            : new Date();
        }

        return {
          headers: { authorization: `Bearer ${tokens.wsToken}` },
        };
      },
      on: {
        connected: (socket) => {
          // If the websocket is open and the token expires in a minute we need to
          // refresh it
          if (
            isAfter(
              new Date(),
              sub(tokens.wsTokenExpiration, { minutes: 1 })
            ) &&
            (socket as WebSocket).readyState === WebSocket.OPEN
          ) {
            (socket as WebSocket).close(CloseCode.Forbidden, 'Forbidden');
          }
        },
        closed: (event) => {
          // If we closed the socket with the Forbidden code we will need to try refreshing the token
          if ((event as CloseEvent).code === CloseCode.Forbidden) {
            tokens.wsToken = null;
          }
        },
      },
    })
  );

  // Send queries and mutations to httpLink and subscriptions to wsLink
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    wsLink,
    httpLink
  );

  const client = new ApolloClient({
    cache,
    link: from([
      errorLink,
      retryLink,
      getHttpUserToken,
      resetExpiredHttpUserToken,
      getHttpAuthHeadersLink,
      splitLink,
    ]),
  });

  const resetClient = async () => {
    tokens.httpToken = null;
    tokens.wsToken = null;
    await client.resetStore();
  };

  return { client, resetClient };
}

export default function useAuthApolloClient(): AuthApolloClient {
  const getAccessToken = useAccessToken();
  const logtail = useLogtail();

  const [cache] = useState(
    () =>
      new InMemoryCache({
        typePolicies: {
          Query: {
            fields: {
              student(_, { args, toReference }) {
                return toReference({
                  __typename: 'students',
                  id: args?.id,
                });
              },
              assignment(_, { args, toReference }) {
                return toReference({
                  __typename: 'assignments',
                  id: args?.id,
                });
              },
              student_assignment(_, { args, toReference }) {
                return toReference({
                  __typename: 'student_assignments',
                  id: args?.id,
                });
              },
            },
          },
        },
      })
  );

  return useMemo(
    () => createAuthApolloClient(cache, getAccessToken, logtail),
    [cache, getAccessToken, logtail]
  );
}
